<template>
  <div class="activity-page">
    <div class="main-content">
      <!-- Top Type Tags -->
      <div class="top-type-tags">
        <div 
          class="type-tag-item" 
          :class="{ active: activityType === 1 }"
          @click="onTypeChange(1)"
        >
          组织活动
        </div>
        <div 
          class="type-tag-item" 
          :class="{ active: activityType === 2 }"
          @click="onTypeChange(2)"
        >
          志愿活动
        </div>
      </div>

      <!-- Top Stats Card -->
      <div class="stats-card">
        <div class="stat-item">
          <div class="num">{{ stats.totalNum || 0 }}</div>
          <div class="label">全部</div>
        </div>
         <div class="stat-item">
          <div class="num">{{ stats.joinTotalNum || 0 }}</div>
          <div class="label">已参与</div>
        </div>
        <div class="stat-item">
          <div class="num">{{ stats.runNum || 0 }}</div>
          <div class="label">进行中</div>
        </div>
        <div class="stat-item">
          <div class="num">{{ stats.oldNum || 0 }}</div>
          <div class="label">已结束</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs-container">
        <el-tabs v-model="activeTab" @tab-change="onTabChange">
          <el-tab-pane label="全部" name="0"></el-tab-pane>
          <el-tab-pane label="待开始" name="1"></el-tab-pane>
          <el-tab-pane label="进行中" name="2"></el-tab-pane>
          <el-tab-pane label="已结束" name="3"></el-tab-pane>
        </el-tabs>
      </div>

      <!-- List -->
      <div class="list-container" v-infinite-scroll="loadMore" :infinite-scroll-disabled="disabled">
        <div class="activity-item" v-for="item in list" :key="item.id" @click="goToDetail(item)">
          <!-- type tag -->
          <div class="type-tag" :style="{ backgroundColor: getTagColor(item.type) }">
            {{ getTagName(item.type) }}
          </div>
          
          <div class="item-header">
            <div class="title">{{ item.activityName }}</div>
          </div>
          
          <div class="item-body">
            <div class="left-img" v-if="getImgUrl(item.imgUrl)">
              <img :src="getImgUrl(item.imgUrl)" alt="">
            </div>
            <div class="right-info">
              <div class="time-header">
                <div class="time-label">{{ item.type === 1 ? '活动时间:' : '报名时间:' }}</div>
                <div class="status-badge" :class="{'participated': item.signFlag > 0}">
                  ({{ getSignFlagText(item.signFlag) }})
                </div>
              </div>
              <div class="time-value">
                {{ item.type === 1 ? formatTime(item.actStartTime) : formatTime(item.signStartTime) }}~{{ item.type === 1 ? formatTime(item.actEndTime) : formatTime(item.signEndTime) }}
              </div>
            </div>
          </div>
          
          <div class="item-footer">
            <div class="publisher">{{ item.actOrgName }}</div>
            <div class="action-btn" v-if="getBtnState(item).show">
              <button 
                class="btn" 
                :class="{
                  'btn-participated': getBtnState(item).text === '已参与' || getBtnState(item).text === '已签到',
                  'btn-disabled': getBtnState(item).disabled && getBtnState(item).text === '我要报名'
                }" 
                @click.stop="handleAction(item, getBtnState(item))"
              >
                {{ getBtnState(item).text }}
              </button>
            </div>
          </div>
        </div>
        <div class="loading-text" v-if="loading">加载中...</div>
        <div class="no-more" v-if="noMore && list.length > 0">没有更多了</div>
        <el-empty v-if="!loading && list.length === 0" description="暂无数据"></el-empty>
      </div>
    </div>
    
    <BottomNav />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import BottomNav from '../component/BottomNav.vue';
import { getActivityList, getActivityStatistics, submitActivitySign, submitActivitySignIn } from '@/api';
import { ElMessage } from 'element-plus';

const router = useRouter();

const STORAGE_KEY = 'activity_page_type';
const activeTab = ref('0');
const activityType = ref(Number(sessionStorage.getItem(STORAGE_KEY)) || 1); // 1: 组织活动, 2: 志愿活动
const list = ref([]);
const page = ref(1);
const pageSize = ref(10);
const total = ref(0);
const loading = ref(false);
const stats = ref({
  oldNum: 0,
  runNum: 0,
  totalNum: 0,
  joinTotalNum: 0
});

const noMore = computed(() => {
  if (total.value === 0) return true;
  return list.value.length >= total.value;
});

const disabled = computed(() => {
  return loading.value || noMore.value;
});

const getList = async (isReset = false) => {
  if (isReset) {
    page.value = 1;
    list.value = [];
  }
  loading.value = true;
  try {
    const params = {
      page: page.value,
      pageSize: pageSize.value,
      type: activityType.value
    };
    if (activeTab.value !== '0') {
      params.dateComparison = activeTab.value;
    }
    const res = await getActivityList(params);
    if (res.stat === 1) {
      list.value = isReset ? res.data.list : [...list.value, ...res.data.list];
      total.value = res.data.total;
    }
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
    if (isReset) {
      nextTick(() => restoreScrollPosition());
    }
  }
};

const getStats = async () => {
  try {
    const res = await getActivityStatistics({ type: activityType.value });
    if (res.stat === 0 || res.stat === 1) {
      stats.value = res.data || { oldNum: 0, runNum: 0, totalNum: 0, joinTotalNum: 0 };
    }
  } catch (error) {
    console.error(error);
  }
};

const onTypeChange = (type) => {
  if (activityType.value === type) return;
  activityType.value = type;
  sessionStorage.setItem(STORAGE_KEY, type);
  activeTab.value = '0';
  getStats();
  getList(true);
};

const onTabChange = () => {
  getList(true);
};

const loadMore = () => {
  if (!loading.value && !noMore.value) {
    page.value++;
    getList();
  }
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  return timeStr;
};

const SCROLL_KEY = 'activity_page_scrollY';

const getScrollPosition = () => {
  const appEl = document.getElementById('app');
  return appEl ? appEl.scrollTop : 0;
};

const setScrollPosition = (y) => {
  const appEl = document.getElementById('app');
  if (appEl) appEl.scrollTop = y;
};

const restoreScrollPosition = () => {
  const saved = sessionStorage.getItem(SCROLL_KEY);
  if (!saved) return;
  const y = parseInt(saved, 10);
  if (y > 0) {
    setScrollPosition(y);
    sessionStorage.removeItem(SCROLL_KEY);
  }
};

const goToDetail = (item) => {
  sessionStorage.setItem(SCROLL_KEY, getScrollPosition());
  router.push({ path: '/activity/detail', query: { objId: item.objId, type: item.type } });
};

const getImgUrl = (imgUrlStr) => {
  if (!imgUrlStr) return '';
  try {
    const imgArray = JSON.parse(imgUrlStr);
    if (Array.isArray(imgArray) && imgArray.length > 0) {
      return imgArray[0].url || '';
    }
  } catch (e) {
    console.error('Error parsing imgUrl:', e);
  }
  return '';
};

const getTagName = (type) => {
  switch (type) {
    case 1: return '组织活动';
    case 2: return '志愿活动';
    case 3: return '投票活动';
    case 4: return '调查问卷';
    default: return '未知活动';
  }
};

const getTagColor = (type) => {
  switch (type) {
    case 1: return '#2b85e4'; // blue
    case 2: return '#2b85e4'; // blue
    case 3: return '#2b85e4'; // blue
    case 4: return '#f5a623'; // orange
    default: return '#2b85e4';
  }
};

const getSignFlagText = (signFlag) => {
  return signFlag > 0 ? '已参与' : '未参与';
};

const getBtnState = (item) => {
  let state = { show: false, text: '', disabled: false, action: '' };

  const now = new Date().getTime();
  const parseDate = (dateStr) => dateStr ? new Date(dateStr.replace(/-/g, '/')).getTime() : 0;

  if (item.type === 1) {
    // 组织活动：按照 actStartTime ~ actEndTime 判断签到
    const actStart = parseDate(item.actStartTime);
    const actEnd = parseDate(item.actEndTime);
    if (item.signFlag === 2) {
      state = { show: false, text: '', disabled: false, action: '' };
    } else if (item.signFlag === 1 || item.signFlag === 0) {
      if (now >= actStart && now <= actEnd) {
        state = { show: true, text: '签到', disabled: false, action: 'signin' };
      } else if (now < actStart) {
        state = { show: true, text: '未到签到时间', disabled: true, action: 'signin_disabled', msg: `签到时间为： ${item.actStartTime} ~ ${item.actEndTime}` };
      } else {
        state = { show: true, text: '已过签到时间', disabled: true, action: 'signin_disabled' };
      }
    }
    return state;
  }

  if (item.type === 3) {
    if (item.signFlag > 0) return { show: true, text: '已参与', disabled: true, action: '' };
    return { show: true, text: '我要投票', disabled: false, action: 'vote' };
  }
  if (item.type === 4) {
    if (item.signFlag > 0) return { show: true, text: '已参与', disabled: true, action: '' };
    return { show: true, text: '填写问卷', disabled: false, action: 'survey' };
  }

  const signStart = parseDate(item.signStartTime);
  const signEnd = parseDate(item.signEndTime);
  const actStart = parseDate(item.actStartTime);
  const actEnd = parseDate(item.actEndTime);

  if (item.signFlag === 0) {
    // 未报名
    if (now >= signStart && now <= signEnd) {
      state = { show: true, text: '我要报名', disabled: false, action: 'signup' };
    } else {
      state = { show: true, text: '我要报名', disabled: true, action: 'signup_disabled' };
    }
  } else if (item.signFlag === 1) {
    // 已报名，未签到
    if (now >= actStart && now <= actEnd) {
      state = { show: true, text: '签到', disabled: false, action: 'signin' };
    } else if (now < actStart) {
      // 未到签到时间
      state = { show: true, text: '未到签到时间', disabled: true, action: 'signin_disabled', msg: `签到时间为：${item.actStartTime} ~ ${item.actEndTime}` };
    } else {
      // 已过签到时间
      state = { show: true, text: '已过签到时间', disabled: true, action: 'signin_disabled' };
    }
  } else if (item.signFlag === 2) {
    // 已签到
    state = { show: false, text: '', disabled: false, action: '' };
  }

  return state;
};

const handleAction = async (item, state) => {
  if (state.disabled) {
    if (state.action === 'signup_disabled') {
      ElMessage.warning('当前不在报名时间段内');
    } else if (state.action === 'signin_disabled') {
      ElMessage.warning(state.msg || state.text);
    }
    return;
  }
  
  if (state.action === 'signup') {
    try {
      const res = await submitActivitySign({
        content: '',
        objId: item.objId,
        type: item.type
      });
      if (res.stat === 1 || res.isSuccess) {
        ElMessage.success('报名成功');
        getStats();
        getList(true);
      } else {
        ElMessage.error(res.msg || '报名失败');
      }
    } catch (error) {
      console.error(error);
      ElMessage.error('报名失败');
    }
  } else if (state.action === 'signin') {
    try {
      const res = await submitActivitySignIn({
        content: '',
        objId: item.objId,
        type: item.type
      });
      if (res.stat === 1 || res.isSuccess) {
        ElMessage.success('签到成功');
        getStats();
        getList(true);
      } else {
        ElMessage.error(res.msg || '签到失败');
      }
    } catch (error) {
      console.error(error);
      ElMessage.error('签到失败');
    }
  }
};

onMounted(() => {
  getStats();
  getList(true);
});
</script>

<style scoped lang="scss">
.activity-page {
  min-height: 100vh;
  background-color: #f4f6f8;
  padding-bottom: 60px;
  box-sizing: border-box;
}

.main-content {
  padding: 15px;
}

.top-type-tags {
  display: flex;
  margin-bottom: 15px;
  
  .type-tag-item {
    flex: 1;
    text-align: center;
    background: #fff;
    padding: 12px 0;
    font-size: 15px;
    color: #333;
    cursor: pointer;
    
    &:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
      border-right: 1px solid #f4f6f8;
    }
    
    &:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    }
    
    &.active {
      color: #e02020;
      font-weight: bold;
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 30px;
        height: 3px;
        background-color: #e02020;
        border-radius: 2px;
      }
    }
  }
}

.stats-card {
  background: #fff;
  border-radius: 10px;
  display: flex;
  justify-content: space-around;
  padding: 20px 0;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  
  .stat-item {
    text-align: center;
    .num {
      color: #e02020;
      font-size: 20px;
      font-weight: bold;
      margin-bottom: 5px;
    }
    .label {
      color: #666;
      font-size: 14px;
    }
  }
}

.tabs-container {
  :deep(.el-tabs__nav-wrap::after) {
    display: none;
  }
  :deep(.el-tabs__item) {
    font-size: 15px;
    color: #666;
    &.is-active {
      color: #333;
      font-weight: bold;
    }
  }
  :deep(.el-tabs__active-bar) {
    background-color: #e02020;
    height: 3px;
    border-radius: 2px;
  }
}

.list-container {
  margin-top: 10px;
}

.activity-item {
  background: #fff;
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 15px;
  position: relative;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  overflow: hidden;
  
  .type-tag {
    position: absolute;
    right: 0;
    top: 0;
    padding: 4px 12px;
    color: #fff;
    font-size: 12px;
    border-bottom-left-radius: 10px;
    &.blue-tag {
      background-color: #2b85e4;
    }
  }
  
  .item-header {
    margin-bottom: 10px;
    padding-right: 60px; // space for type-tag
    .title {
      font-size: 16px;
      font-weight: bold;
      color: #333;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  
  .item-body {
    display: flex;
    margin-bottom: 10px;
    
    .left-img {
      width: 90px;
      height: 90px;
      border-radius: 6px;
      overflow: hidden;
      flex-shrink: 0;
      margin-right: 12px;
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    
    .right-info {
      flex: 1;
      position: relative;
      display: flex;
      flex-direction: column;
      
      .time-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 4px;
        
        .time-label {
          font-size: 13px;
          color: #333;
        }
        
        .status-badge {
          font-size: 12px;
          color: #999;
          border: 1px solid #e0e0e0;
          padding: 2px 8px;
          border-radius: 12px;
          background: #fff;
          &.participated {
            color: #2b85e4;
            border-color: #2b85e4;
          }
        }
      }
      
      .time-value {
        font-size: 12px;
        color: #999;
        line-height: 1.4;
        word-break: break-all;
      }
    }
  }
  
  .item-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 5px;
    
    .publisher {
      font-size: 12px;
      color: #999;
    }
    
    .action-btn {
      .btn {
        background-color: #f56c6c;
        color: #fff;
        border: none;
        padding: 6px 16px;
        border-radius: 16px;
        font-size: 13px;
        cursor: pointer;
        outline: none;
        &.btn-participated {
          background-color: #fff;
          color: #f56c6c;
          border: 1px solid #f56c6c;
        }
        &.btn-disabled {
          background-color: #f5f5f5;
          color: #999;
          border: 1px solid #d9d9d9;
          cursor: not-allowed;
        }
      }
    }
  }
}

.loading-text, .no-more {
  text-align: center;
  color: #999;
  font-size: 13px;
  padding: 10px 0;
}
</style>